import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUserFriends,
  FaShip,
  FaRulerCombined,
  FaChevronLeft,
  FaChevronRight,
  FaEuroSign,
  FaBuilding,
  FaCalendarAlt,
  FaAnchor,
  FaTag,
  FaRegCheckCircle,
  FaMoneyBillWave,
} from "react-icons/fa";
import { GiCaptainHatProfile } from "react-icons/gi";
import { IoCalendarOutline } from "react-icons/io5";
import { motion, AnimatePresence } from "framer-motion";
import { yachts as yachtApi } from "../../services/api"; // Import the yacht service
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import { format, parseISO } from 'date-fns'; // For formatting date

const ITEMS_PER_PAGE = 5; // Adjust as needed

// Helper to find lowest price from pricing structure
const findLowestPrice = (yacht) => {
  if (!yacht.pricing) return null;
  
  let lowestPrice = Infinity;
  let priceType = '';
  
  // Check private half day prices
  if (yacht.pricing.private?.halfDay) {
    if (yacht.pricing.private.halfDay.lowSeason && yacht.pricing.private.halfDay.lowSeason < lowestPrice) {
      lowestPrice = yacht.pricing.private.halfDay.lowSeason;
      priceType = 'Private Half Day (Low Season)';
    }
    if (yacht.pricing.private.halfDay.mediumSeason && yacht.pricing.private.halfDay.mediumSeason < lowestPrice) {
      lowestPrice = yacht.pricing.private.halfDay.mediumSeason;
      priceType = 'Private Half Day (Mid Season)';
    }
    if (yacht.pricing.private.halfDay.highSeason && yacht.pricing.private.halfDay.highSeason < lowestPrice) {
      lowestPrice = yacht.pricing.private.halfDay.highSeason;
      priceType = 'Private Half Day (High Season)';
    }
  }
  
  // Check private full day prices
  if (yacht.pricing.private?.fullDay) {
    if (yacht.pricing.private.fullDay.lowSeason && yacht.pricing.private.fullDay.lowSeason < lowestPrice) {
      lowestPrice = yacht.pricing.private.fullDay.lowSeason;
      priceType = 'Private Full Day (Low Season)';
    }
    if (yacht.pricing.private.fullDay.mediumSeason && yacht.pricing.private.fullDay.mediumSeason < lowestPrice) {
      lowestPrice = yacht.pricing.private.fullDay.mediumSeason;
      priceType = 'Private Full Day (Mid Season)';
    }
    if (yacht.pricing.private.fullDay.highSeason && yacht.pricing.private.fullDay.highSeason < lowestPrice) {
      lowestPrice = yacht.pricing.private.fullDay.highSeason;
      priceType = 'Private Full Day (High Season)';
    }
  }
  
  // Check semi-private prices
  if (yacht.pricing.semiPrivate?.halfDay) {
    if (yacht.pricing.semiPrivate.halfDay.lowSeason && yacht.pricing.semiPrivate.halfDay.lowSeason < lowestPrice) {
      lowestPrice = yacht.pricing.semiPrivate.halfDay.lowSeason;
      priceType = 'Semi-Private Half Day (Low Season)';
    }
    if (yacht.pricing.semiPrivate.halfDay.mediumSeason && yacht.pricing.semiPrivate.halfDay.mediumSeason < lowestPrice) {
      lowestPrice = yacht.pricing.semiPrivate.halfDay.mediumSeason;
      priceType = 'Semi-Private Half Day (Mid Season)';
    }
    if (yacht.pricing.semiPrivate.halfDay.highSeason && yacht.pricing.semiPrivate.halfDay.highSeason < lowestPrice) {
      lowestPrice = yacht.pricing.semiPrivate.halfDay.highSeason;
      priceType = 'Semi-Private Half Day (High Season)';
    }
  }
  
  if (yacht.pricing.semiPrivate?.fullDay) {
    if (yacht.pricing.semiPrivate.fullDay.lowSeason && yacht.pricing.semiPrivate.fullDay.lowSeason < lowestPrice) {
      lowestPrice = yacht.pricing.semiPrivate.fullDay.lowSeason;
      priceType = 'Semi-Private Full Day (Low Season)';
    }
    if (yacht.pricing.semiPrivate.fullDay.mediumSeason && yacht.pricing.semiPrivate.fullDay.mediumSeason < lowestPrice) {
      lowestPrice = yacht.pricing.semiPrivate.fullDay.mediumSeason;
      priceType = 'Semi-Private Full Day (Mid Season)';
    }
    if (yacht.pricing.semiPrivate.fullDay.highSeason && yacht.pricing.semiPrivate.fullDay.highSeason < lowestPrice) {
      lowestPrice = yacht.pricing.semiPrivate.fullDay.highSeason;
      priceType = 'Semi-Private Full Day (High Season)';
    }
  }
  
  return lowestPrice === Infinity ? null : { price: lowestPrice, type: priceType };
};

const SearchResultsList = ({ filters }) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [allYachts, setAllYachts] = useState([]); // Store all fetched yachts
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all yachts once on component mount
  useEffect(() => {
    const fetchAllYachts = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await yachtApi.getAll();
        const fetchedYachts = response.data.map((yacht) => {
          // Get the first image URL if available
          let imageSrc = '';
          if (yacht.images && yacht.images.length > 0) {
            const imagePath = yacht.images[0];
            if (imagePath.includes('/uploads/')) {
              imageSrc = `${'https://yacht-server.create-site.gr'}${imagePath}`;
            }
          }

          // Calculate lowest price
          const lowestPriceInfo = findLowestPrice(yacht);

          return {
            ...yacht,
            image: yacht.image || imageSrc,
            lowestPriceInfo, // Add lowest price
            // Ensure numeric fields are correctly parsed
            price: parseFloat(yacht.price) || 0,
            length: parseFloat(yacht.specifications?.length) || yacht.length || 0,
            cabins: parseInt(yacht.cabins, 10) || 0,
            capacity: parseInt(yacht.guestCapacity || yacht.capacity, 10) || 0,
          };
        });
        setAllYachts(fetchedYachts);
      } catch (err) {
        console.error("Error fetching yachts:", err);
        setError("Failed to fetch yachts. Please try refreshing the page.");
      } finally {
        setLoading(false);
      }
    };

    fetchAllYachts();
  }, []); // Empty dependency array means this runs only once

  console.log("Fetched Yachts:", allYachts); // Debugging line to check fetched yachts

  // Client-side filtering logic based on the filters prop
const filteredResults = useMemo(() => {
  // Start filtering only after initial load is complete
  if (loading) return [];

  let results = [...allYachts];
  console.log("Starting filtering with", results.length, "yachts");

  console.log(filters, "FILTERS")

  // Check if filters object exists and has properties before applying filters
  if (!filters || Object.keys(filters).length === 0) {
    return results; // Return all yachts if no filters are set
  }

  // Apply filters one by one
  if (filters.yachtType && filters.yachtType.length > 0) {
    results = results.filter(yacht => 
      filters.yachtType.includes(yacht.yachtType || yacht.type)
    );
    console.log("After yacht type filter:", results.length);
  }

  if (filters.priceRange && Array.isArray(filters.priceRange) && filters.priceRange.length === 2) {
    const [minPrice, maxPrice] = filters.priceRange;
    // Only apply filter if valid price range values exist
    if (typeof minPrice === 'number' && typeof maxPrice === 'number') {
      results = results.filter(yacht => 
        (yacht.price >= minPrice && yacht.price <= maxPrice)
      );
      console.log("After price filter:", results.length);
    }
  }

  if (filters.lengthRange && Array.isArray(filters.lengthRange) && filters.lengthRange.length === 2) {
    const [minLength, maxLength] = filters.lengthRange;
    // Only apply filter if valid length range values exist
    if (typeof minLength === 'number' && typeof maxLength === 'number') {
      results = results.filter(yacht => {
        const yachtLength = yacht.length || 
          (yacht.specifications && parseFloat(yacht.specifications.length)) || 0;
        return yachtLength >= minLength && yachtLength <= maxLength;
      });
      console.log("After length filter:", results.length);
    }
  }

  if (filters.cabins && filters.cabins.toString().trim() !== '') {
    const minCabins = parseInt(filters.cabins.toString().replace('+', ''), 10);
    if (!isNaN(minCabins)) {
      results = results.filter(yacht => yacht.cabins >= minCabins);
      console.log("After cabins filter:", results.length);
    }
  }

  if (filters.numberOfGuests && !isNaN(parseInt(filters.numberOfGuests, 10))) {
    const numGuests = parseInt(filters.numberOfGuests, 10);
    results = results.filter(yacht => 
      (yacht.capacity >= numGuests) || (yacht.guestCapacity >= numGuests)
    );
    console.log("After guests filter:", results.length);
  }

  // Destination filter (check multiple possible properties)
  if (filters.destination && filters.destination.trim() !== '') {
    const destinationLower = filters.destination.toLowerCase().trim();
    results = results.filter(yacht =>
      (yacht.availableDestinations?.some(dest => 
        dest.toLowerCase().trim() === destinationLower)) ||
      (yacht.destinations?.available?.some(dest => 
        dest.toLowerCase().trim() === destinationLower))
    );
    console.log("After destination filter:", results.length);
  }

  // Cruise Type filter (check cruiseOptions)
  if (filters.cruiseType && filters.cruiseType.trim() !== '') {
    const cruiseTypeLower = filters.cruiseType.toLowerCase().trim();
    results = results.filter(yacht =>
      (yacht.supportedCruiseTypes?.some(type => 
        type.toLowerCase().trim() === cruiseTypeLower)) ||
      (yacht.cruiseOptions?.some(option => 
        option.type?.toLowerCase().trim() === cruiseTypeLower))
    );
    console.log("After cruise type filter:", results.length);
  }

  return results;
}, [allYachts, filters, loading]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
    // Optionally scroll to top
    // window.scrollTo({ top: 0, behavior: "smooth" });
  }, [filters]);

  // Pagination logic
  const totalPages = Math.ceil(filteredResults.length / ITEMS_PER_PAGE);
  const paginatedResults = useMemo(() => {
      return filteredResults.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
      );
  }, [filteredResults, currentPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      // Scroll to the top of the list when changing pages
      const listTop = document.getElementById('search-results-list-top');
      if (listTop) {
          listTop.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  // Handle clicking on a yacht card
  const handleViewDetails = (yachtId) => {
    // Navigate to yacht details page, passing current filters as state
    // This allows the details page to pre-fill booking info if needed
    navigate(`/yachts/${yachtId}`, { state: { filters } });
  };

  // Helper to format date safely
  const formatDate = (dateString) => {
      try {
          return format(parseISO(dateString), 'PPP');
      } catch (e) {
          return ""; // Return empty if date is invalid
      }
  }

  if (loading && allYachts.length === 0) { // Show spinner only on initial load
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="text-center text-red-500 py-10 JosefinSans">{error}</div>;
  }

  console.log("Filtered Results:", filteredResults); // Debugging line to check filtered results

  return (
    <div className="JosefinSans" id="search-results-list-top">
      <AnimatePresence mode="wait">
        {paginatedResults.length > 0 ? (
          <motion.div
            key={currentPage}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {paginatedResults.map((yacht, index) => (
              <motion.div
                key={yacht._id || index}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="mb-6"
              >
                {/* Enhanced yacht card with more details */}
                <div className="bg-white rounded-xl shadow-lg overflow-hidden flex flex-col md:flex-row hover:shadow-xl transition-shadow duration-300">
                  {/* Image section */}
                  <div className="md:w-1/3 lg:w-2/5 relative bg-gray-200">
                    {yacht.images ? (
                      <img
                        src={`https://yacht-server.create-site.gr${yacht.images[0]}`}
                        alt={yacht.name}
                        className="w-full h-full max-h-80 object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://placehold.co/600x400?text=Yacht';
                        }}
                      />
                    ) : (
                      <div className="w-full h-48 md:h-full flex items-center justify-center bg-gray-100">
                        <FaShip className="text-gray-400 text-4xl" />
                      </div>
                    )}
                    
                    {/* Status badge */}
                    {yacht.status && (
                      <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-semibold
                        ${yacht.status === 'available' ? 'bg-green-100 text-green-800' : 
                          yacht.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-red-100 text-red-800'}`}>
                        {yacht.status.charAt(0).toUpperCase() + yacht.status.slice(1)}
                      </div>
                    )}
                  </div>
                  
                  {/* Content section */}
                  <div className="p-5 md:w-2/3 lg:w-3/5 flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-start">
                        <h3 className="text-2xl font-bold text-blue-900 mb-1">{yacht.builder} {yacht.name}</h3>
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                          {yacht.yachtType || yacht.type}
                        </span>
                      </div>
                      
                      {/* Builder and year built */}
                      <div className="flex items-center text-sm text-gray-600 mb-2">
                        <FaBuilding className="mr-2 text-gray-500" />
                        <span>{yacht.builder}</span>
                        {yacht.yearBuilt && (
                          <span className="ml-2 flex items-center">
                            <FaCalendarAlt className="mr-1" />Built: {yacht.yearBuilt}
                          </span>
                        )}
                      </div>
                      
                      {/* Description preview */}
                      {yacht.description && (
                        <p className="text-gray-600 text-sm mb-3 line-clamp-1">
                          {yacht.description}
                        </p>
                      )}
                      
                      {/* Specifications grid */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-sm text-gray-700 mb-3">
                        {/* Length */}
                        <div className="flex items-center">
                          <FaRulerCombined className="mr-2 text-blue-500" /> 
                          {yacht.specifications?.length || yacht.length || "N/A"} Length
                        </div>
                        
                        {/* Beam */}
                        {yacht.specifications?.beam && (
                          <div className="flex items-center">
                            <FaShip className="mr-2 text-blue-500" /> 
                            {yacht.specifications.beam} Beam
                          </div>
                        )}
                        
                        {/* Draft */}
                        {yacht.specifications?.draft && (
                          <div className="flex items-center">
                            <FaAnchor className="mr-2 text-blue-500" /> 
                            {yacht.specifications.draft} Draft
                          </div>
                        )}
                        
                        {/* Guest capacity */}
                        <div className="flex items-center">
                          <FaUserFriends className="mr-2 text-blue-500" /> 
                          Up to {yacht.guestCapacity || yacht.capacity || 0} Guests
                        </div>
                        
                        {/* Crew capacity */}
                        {yacht.crewCapacity > 0 && (
                          <div className="flex items-center">
                            <GiCaptainHatProfile className="mr-2 text-blue-500" /> 
                            {yacht.crewCapacity} Crew
                          </div>
                        )}
                        
                        {/* Total units */}
                        {/* {yacht.totalUnits > 0 && (
                          <div className="flex items-center">
                            <FaTag className="mr-2 text-blue-500" /> 
                            {yacht.totalUnits} Units Available
                          </div>
                        )} */}
                      </div>
                      
                      {/* Included items preview */}
                      {yacht.includedItems && yacht.includedItems.length > 0 && (
                        <div className="flex items-center text-sm text-gray-600 mb-2">
                          <FaRegCheckCircle className="mr-2 text-green-500" />
                          <span className="truncate">
                            <span className="font-medium">Includes:</span> {yacht.includedItems.slice(0, 2).join(', ')}
                            {yacht.includedItems.length > 2 ? '...' : ''}
                          </span>
                        </div>
                      )}
                      
                      {/* Display date if available in filters */}
                      {filters.bookingDate && (
                        <div className="flex items-center text-blue-600 font-medium text-sm mt-1">
                          <IoCalendarOutline className="mr-2" />
                          {filters.bookingDate instanceof Date 
                            ? format(filters.bookingDate, 'PPP') 
                            : formatDate(filters.bookingDate)}
                          {filters.bookingTime && ` at ${filters.bookingTime}`}
                        </div>
                      )}
                    </div>
                    
                    {/* Pricing and action button */}
                              <div className="flex flex-col sm:flex-row items-center justify-between mt-3 pt-3 border-t">
                                {/* Enhanced pricing display */}
                                <div className="flex flex-col mb-3 sm:mb-0">
                                <span className="text-xs text-gray-500 flex items-center">
                                  <FaMoneyBillWave className="mr-1 text-green-600" /> Price options:
                                </span>
                                
                                {yacht.pricing ? (
                                  <>
                                  <div className="text-xl font-bold text-blue-800 flex items-center">
                                    <FaEuroSign className="inline mr-0.5 text-lg" />
                                    {(() => {
                                    // Find the lowest rate available
                                    let lowestRate = Infinity;
                                    let rateLabel = "";
                                    
                                    // Check private bookings
                                    if (yacht.pricing.private) {
                                      // Check half-day options
                                      if (yacht.pricing.private.halfDay) {
                                      const seasons = ['lowSeason', 'mediumSeason', 'highSeason'];
                                      seasons.forEach(season => {
                                        if (yacht.pricing.private.halfDay[season]?.guestTiers?.length > 0) {
                                        const minTierRate = Math.min(...yacht.pricing.private.halfDay[season].guestTiers.map(t => t.rate));
                                        if (minTierRate < lowestRate) {
                                          lowestRate = minTierRate;
                                          rateLabel = `Private Half-Day (${season.replace('Season', '')})`;
                                        }
                                        }
                                      });
                                      }
                                      
                                      // Check full-day options
                                      if (yacht.pricing.private.fullDay) {
                                      const seasons = ['lowSeason', 'mediumSeason', 'highSeason'];
                                      seasons.forEach(season => {
                                        if (yacht.pricing.private.fullDay[season]?.guestTiers?.length > 0) {
                                        const minTierRate = Math.min(...yacht.pricing.private.fullDay[season].guestTiers.map(t => t.rate));
                                        if (minTierRate < lowestRate) {
                                          lowestRate = minTierRate;
                                          rateLabel = `Private Full-Day (${season.replace('Season', '')})`;
                                        }
                                        }
                                      });
                                      }
                                    }
                                    
                                    // Check semi-private bookings
                                    if (yacht.pricing.semiPrivate) {
                                      // Check half-day options with perGuestRate
                                      if (yacht.pricing.semiPrivate.halfDay) {
                                      const seasons = ['lowSeason', 'mediumSeason', 'highSeason'];
                                      seasons.forEach(season => {
                                        if (yacht.pricing.semiPrivate.halfDay[season]?.perGuestRate) {
                                        const rate = yacht.pricing.semiPrivate.halfDay[season].perGuestRate;
                                        if (rate < lowestRate) {
                                          lowestRate = rate;
                                          rateLabel = `Semi-Private Half-Day (${season.replace('Season', '')})`;
                                        }
                                        }
                                      });
                                      }
                                      
                                      // Check full-day options with perGuestRate
                                      if (yacht.pricing.semiPrivate.fullDay) {
                                      const seasons = ['lowSeason', 'mediumSeason', 'highSeason'];
                                      seasons.forEach(season => {
                                        if (yacht.pricing.semiPrivate.fullDay[season]?.perGuestRate) {
                                        const rate = yacht.pricing.semiPrivate.fullDay[season].perGuestRate;
                                        if (rate < lowestRate) {
                                          lowestRate = rate;
                                          rateLabel = `Semi-Private Full-Day (${season.replace('Season', '')})`;
                                        }
                                        }
                                      });
                                      }
                                    }
                                    
                                    return lowestRate === Infinity 
                                      ? "Contact for pricing" 
                                      : lowestRate.toLocaleString();
                                    })()}
                                  </div>
                                  <span className="text-xs text-gray-500">
                                    {(() => {
                                    // Same logic to get the label for the lowest price
                                    let lowestRate = Infinity;
                                    let rateLabel = "";
                                    let perPerson = false;
                                    
                                    // Check private bookings
                                    if (yacht.pricing.private) {
                                      // Check half-day options
                                      if (yacht.pricing.private.halfDay) {
                                      const seasons = ['lowSeason', 'mediumSeason', 'highSeason'];
                                      seasons.forEach(season => {
                                        if (yacht.pricing.private.halfDay[season]?.guestTiers?.length > 0) {
                                        const minTier = yacht.pricing.private.halfDay[season].guestTiers.reduce(
                                          (min, tier) => tier.rate < min.rate ? tier : min,
                                          { rate: Infinity }
                                        );
                                        if (minTier.rate < lowestRate) {
                                          lowestRate = minTier.rate;
                                          rateLabel = `Private Half-Day (${season.replace('Season', '')}) for ${minTier.guests} guests`;
                                        }
                                        }
                                      });
                                      }
                                      
                                      // Check full-day options
                                      if (yacht.pricing.private.fullDay) {
                                      const seasons = ['lowSeason', 'mediumSeason', 'highSeason'];
                                      seasons.forEach(season => {
                                        if (yacht.pricing.private.fullDay[season]?.guestTiers?.length > 0) {
                                        const minTier = yacht.pricing.private.fullDay[season].guestTiers.reduce(
                                          (min, tier) => tier.rate < min.rate ? tier : min,
                                          { rate: Infinity }
                                        );
                                        if (minTier.rate < lowestRate) {
                                          lowestRate = minTier.rate;
                                          rateLabel = `Private Full-Day (${season.replace('Season', '')}) for ${minTier.guests} guests`;
                                        }
                                        }
                                      });
                                      }
                                    }
                                    
                                    // Check semi-private bookings
                                    if (yacht.pricing.semiPrivate) {
                                      // Check half-day options with perGuestRate
                                      if (yacht.pricing.semiPrivate.halfDay) {
                                      const seasons = ['lowSeason', 'mediumSeason', 'highSeason'];
                                      seasons.forEach(season => {
                                        if (yacht.pricing.semiPrivate.halfDay[season]?.perGuestRate) {
                                        const rate = yacht.pricing.semiPrivate.halfDay[season].perGuestRate;
                                        if (rate < lowestRate) {
                                          lowestRate = rate;
                                          rateLabel = `Semi-Private Half-Day (${season.replace('Season', '')})`;
                                          perPerson = true;
                                        }
                                        }
                                      });
                                      }
                                      
                                      // Check full-day options with perGuestRate
                                      if (yacht.pricing.semiPrivate.fullDay) {
                                      const seasons = ['lowSeason', 'mediumSeason', 'highSeason'];
                                      seasons.forEach(season => {
                                        if (yacht.pricing.semiPrivate.fullDay[season]?.perGuestRate) {
                                        const rate = yacht.pricing.semiPrivate.fullDay[season].perGuestRate;
                                        if (rate < lowestRate) {
                                          lowestRate = rate;
                                          rateLabel = `Semi-Private Full-Day (${season.replace('Season', '')})`;
                                          perPerson = true;
                                        }
                                        }
                                      });
                                      }
                                    }
                                    
                                    return lowestRate === Infinity 
                                      ? "Please inquire for details" 
                                      : `${rateLabel}${perPerson ? ' per person' : ''}`;
                                    })()}
                                  </span>
                                  </>
                                ) : (
                                  <div className="text-lg font-bold text-blue-800">
                                  Contact for Pricing
                                  </div>
                                )}
                                </div>
                                
                                {/* Action button */}
                                <button
                                onClick={() => handleViewDetails(yacht._id)}
                                className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-5 py-2 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-800 transition duration-300 shadow-md hover:shadow-lg w-full sm:w-auto text-sm"
                                >
                                View Details & Book
                                </button>
                              </div>
                              </div>
                            </div>
                            </motion.div>
                          ))}
                          </motion.div>
                        ) : (
          // Show 'No results' only if not loading and filters have been applied
          !loading && (
            <motion.div
              key="no-results"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-16 text-gray-500"
            >
              No yachts found matching your criteria.
            </motion.div>
          )
        )}
      </AnimatePresence>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-2 mt-8 pb-4">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-2 rounded-full bg-white shadow hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
            aria-label="Previous Page"
          >
            <FaChevronLeft className="text-blue-600 h-4 w-4" />
          </button>
          <span className="text-gray-700 font-medium text-sm">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-2 rounded-full bg-white shadow hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
            aria-label="Next Page"
          >
            <FaChevronRight className="text-blue-600 h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default SearchResultsList;